/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, June 2023
 * @flow
 */

import React from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import {
  Box,
  IconButton,
  Paper,
  Typography,
} from '@material-ui/core';
import {
  DeleteOutlined as DeleteIcon,
  EditOutlined as EditIcon,
  SmsOutlined as SmsIcon,
  DescriptionOutlined as DescriptionIcon,
  SendOutlined as SendIcon,
  KeyboardReturnOutlined as KeyboardReturnIcon,
} from '@material-ui/icons';
import useStyles from './useStyles';
import FireboardUtils from '../../../../util/fireboard-util';
import { getPickerData } from "../../../../components/CountryPicker";

const WaitlistCard = ({
  index,
  booking,
  onBookingActionClick,
  stationMap,
}) => {
  const [showNotes, setShowNotes] = React.useState(false);
  const { labelsData } = useSelector(state => state);
  const labels = labelsData?.labels;

  const classes = useStyles();
  const countriesData = getPickerData();

  const toggleNotes = () => {
    setShowNotes(showNotes => !showNotes);
  };

  const getFormattedPhnNum = (booking) => {
    const { phoneNumber, countryCode } = booking;
    let formattedNumber;
    const dialCode = countryCode
      ? countryCode.includes('+')
        ? countryCode
        : `+${countryCode}`
      : '+1';
    const country = countriesData.find(country => country.dialCode == dialCode);
    let iso2 = 'US';
    if (!FireboardUtils.isObjectBlank(country))
      iso2 = (country?.iso2).toUpperCase();
    formattedNumber = FireboardUtils.formattedPhoneNumber(phoneNumber, iso2);
    return formattedNumber;
  };

  const getStationName = (stationId) => {
    if (stationMap && stationMap.has(stationId)) {
      return stationMap.get(stationId)?.number;
    }
    return null;
  };

  const hasBookingNotes = () => {
    const { note } = booking;
    if (note !== null && note !== undefined && String(booking.note).trim() !== '')
      return true;
    return false;
  };

  const hasBookingComments = () => {
    const { comment } = booking;
    if (comment !== null && comment !== undefined && String(booking.comment).trim() !== '')
      return true;
    return false;
  };

  const isNoteIconVisible = () => {
    return hasBookingNotes() || hasBookingComments();
  };

  const onClickActionBtn = actionType => {
    onBookingActionClick && onBookingActionClick(actionType, booking);
  };

  const stationName = getStationName(booking.stationId);

  return (
    <Paper elevation={0} className={classes.listPaper}>
      <Box display={'flex'}>
        <Box width={'7%'}>
          <Typography variant='caption' className={classes.text}>{index + 1}.</Typography>
        </Box>
        <Box width={'41%'}>
          <Typography variant='body1' noWrap className={classes.text}>{booking.name}</Typography>
          <Typography variant='subtitle2' noWrap className={classes.text}>{getFormattedPhnNum(booking)}</Typography>
          <Typography variant='subtitle2' noWrap className={classes.text}>{booking.emailAddress}</Typography>
          {(booking?.status === 7 && getStationName(booking?.stationId)) && (
            <Typography variant='subtitle2' noWrap className={classes.text}>#{stationName}</Typography>
          )}
        </Box>
        <Box width={'52%'} display={'flex'} height={'45px'} justifyContent={'flex-end'}>
          <Box display={'flex'} justifyContent={'flex-end'} alignItems={'center'}>
            {isNoteIconVisible() && (
              <IconButton size='small' color='primary' className={cn(classes.commonActionBtn, classes.noteBtn)} onClick={toggleNotes}>
                <DescriptionIcon fontSize='inherit' />
              </IconButton>
            )}
            {booking?.status === 0 && (
              <IconButton size='small' className={cn(classes.commonActionBtn, classes.actionBtn)} onClick={() => onClickActionBtn('cancelWaitlist')}>
                <DeleteIcon fontSize='inherit' />
              </IconButton>
            )}
            <IconButton size='small' className={cn(classes.commonActionBtn, classes.actionBtn)} onClick={() => onClickActionBtn('editWaitlist')} >
              <EditIcon fontSize='inherit' />
            </IconButton>
            <IconButton size='small' className={cn(classes.commonActionBtn, classes.actionBtn)} onClick={() => onClickActionBtn('smsGuest')}>
              <SmsIcon fontSize='inherit' />
            </IconButton>
            <IconButton className={cn(classes.commonActionBtn, classes.partySizeBtn)}>
              <Typography variant='h6'>{booking.partySize}</Typography>
            </IconButton>
            <IconButton size='small' className={cn(classes.commonActionBtn, classes.sendBtn)} onClick={() => onClickActionBtn(booking?.status === 0 ? 'seatParty' : 'clearSeating')}>
              {booking?.status === 0
                ? <SendIcon fontSize='small' />
                : (
                  <Box>
                    <Typography variant='subtitle2'>{stationName}</Typography>
                    <KeyboardReturnIcon fontSize='small' />
                  </Box>
                )
              }
            </IconButton>
          </Box>
        </Box>
      </Box>
      {showNotes && (
        <React.Fragment>
          {hasBookingNotes() && (
            <Box className={classes.notesWrapper}>
              <Typography variant='subtitle2' noWrap className={classes.text}>{labels.WAITLIST_NOTES}</Typography>
              <Typography variant='caption' noWrap className={classes.text}>{booking.note}</Typography>
            </Box>
          )}
          {hasBookingComments() && (
            <Box className={classes.notesWrapper}>
              <Typography variant='subtitle2' noWrap className={classes.text}>{labels.WAITLIST_STAFF_COMMENTS}</Typography>
              <Typography variant='caption' noWrap className={classes.text}>{booking.comment}</Typography>
            </Box>
          )}
        </React.Fragment>
      )}
    </Paper>
  );
};

export default WaitlistCard;
